import { useEffect, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "./Icon.tsx";
import type { WeekStart } from "../lib/calendar-prefs.ts";

interface Props {
  /** Currently-selected date (highlighted with the accent ring). */
  value: Date;
  /** Click on a day → fired with the local-YYYY-MM-DD string. */
  onChange: (date: Date) => void;
  onClose: () => void;
  /** Anchor element's bounding rect, for popover positioning. */
  anchor: DOMRect;
  /** Optional: days with at least one event get a dot under the date number. */
  appointmentCounts?: Map<string, number>;
  weekStart: WeekStart;
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function monthGrid(year: number, month: number, weekStart: WeekStart): Date[] {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() - weekStart + 7) % 7;
  const start = new Date(year, month, 1 - offset);
  // 6 rows × 7 cols = 42 cells covers any month layout.
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export function MiniMonthPicker({ value, onChange, onClose, anchor, appointmentCounts, weekStart }: Props) {
  const [view, setView] = useState<{ y: number; m: number }>(() => ({
    y: value.getFullYear(),
    m: value.getMonth(),
  }));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const dayHeaders = (() => {
    const labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return Array.from({ length: 7 }, (_, i) => labels[(weekStart + i) % 7]);
  })();

  const cells = monthGrid(view.y, view.m, weekStart);
  const todayISO = ymd(new Date());
  const valueISO = ymd(value);

  const popoverWidth = 280;
  const popoverHeight = 290;
  const margin = 8;
  let left = anchor.left;
  let top = anchor.bottom + margin;
  if (left + popoverWidth > window.innerWidth - margin) {
    left = window.innerWidth - popoverWidth - margin;
  }
  if (top + popoverHeight > window.innerHeight - margin) {
    top = anchor.top - popoverHeight - margin;
  }

  const monthLabel = new Date(view.y, view.m, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 90 }} />
      <div
        role="dialog"
        aria-label="Pick a date"
        style={{
          position: "fixed",
          top,
          left,
          width: popoverWidth,
          background: "var(--paper-raised)",
          border: "1px solid var(--rule)",
          boxShadow: "var(--shadow-md)",
          borderRadius: 12,
          padding: 12,
          zIndex: 100,
        }}
      >
        {/* Month header with prev/next chevrons */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <button
            type="button"
            onClick={() => {
              const next = view.m === 0 ? { y: view.y - 1, m: 11 } : { y: view.y, m: view.m - 1 };
              setView(next);
            }}
            className="zd-icon-btn"
            aria-label="Previous month"
          >
            <ChevronLeftIcon size={14} />
          </button>
          <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 15, fontWeight: 500, color: "var(--ink)" }}>
            {monthLabel}
          </div>
          <button
            type="button"
            onClick={() => {
              const next = view.m === 11 ? { y: view.y + 1, m: 0 } : { y: view.y, m: view.m + 1 };
              setView(next);
            }}
            className="zd-icon-btn"
            aria-label="Next month"
          >
            <ChevronRightIcon size={14} />
          </button>
        </div>

        {/* Day-of-week headers */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 4 }}>
          {dayHeaders.map((h) => (
            <div
              key={h}
              style={{
                textAlign: "center",
                fontSize: 10,
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                color: "var(--ink-faint)",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                padding: "4px 0",
              }}
            >
              {h}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {cells.map((d, i) => {
            const inMonth = d.getMonth() === view.m;
            const iso = ymd(d);
            const isToday = iso === todayISO;
            const isSelected = iso === valueISO;
            const hasEvents = (appointmentCounts?.get(iso) ?? 0) > 0;
            return (
              <button
                key={i}
                type="button"
                onClick={() => {
                  onChange(d);
                  onClose();
                }}
                style={{
                  position: "relative",
                  background: isSelected ? "var(--ink)" : "transparent",
                  color: isSelected
                    ? "var(--canvas)"
                    : !inMonth
                      ? "var(--ink-faint)"
                      : isToday
                        ? "var(--accent-ink)"
                        : "var(--ink)",
                  border: isToday && !isSelected ? "1px solid var(--accent)" : "1px solid transparent",
                  borderRadius: 8,
                  padding: "8px 0",
                  fontSize: 13,
                  fontWeight: isSelected || isToday ? 600 : 400,
                  fontVariantNumeric: "tabular-nums",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = "var(--paper-well)";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = "transparent";
                }}
              >
                {d.getDate()}
                {hasEvents && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: 3,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      background: isSelected ? "var(--canvas)" : "var(--accent)",
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
